import * as Sentry from "@sentry/core";
import { createDebugLogger } from "./debugLog.js";
/**
 * Instrumentation of the app startup sequence (bundle evaluation, init API
 * calls, event stream start, sync wait...).
 *
 * Phases are recorded relative to `performance.timeOrigin` (navigation start)
 * and reported once, when the app becomes usable, as a single "app.startup"
 *
 * Post init phase (like ai) are registerd as s
 */
const debug = createDebugLogger("load-perf");
// Console summary is shown in dev (Vte replaces import.meta.env.DEV at
// build time; the fallback covers non-Vite consumers) or when explicitly
// enabled with localStorage.debug = "load-perf"
const shouldLogToConsole = () => Boolean(import.meta.env?.DEV || debug.enabled());
const phases = [];
let reported = false;
/** Test-only: clears the accumulated phases and the one-shot report guard. */
export function _resetLoadPerf() {
    phases.length = 0;
    reported = false;
}
/** Record a phase whose start time is already in the past */
export function recordLoadPhase(name, start, end, data) {
    if (reported)
        return;
    phases.push({ name, start, end, data });
}
/**
 * Start a phase now; returns a function ending it. Ending twice is a no-op.
 * Phases still open when the report is sent are ended at report time.
 */
export function startLoadPhase(name, data) {
    if (reported)
        return () => { };
    const phase = { name, start: performance.now(), data };
    phases.push(phase);
    return (extraData) => {
        if (phase.end)
            return;
        phase.end = performance.now();
        if (extraData)
            phase.data = { ...phase.data, ...extraData };
    };
}
export async function trackLoadPhase(name, fn, data) {
    if (reported)
        return fn();
    const phase = { name, start: performance.now(), data };
    phases.push(phase);
    try {
        return await Sentry.startSpan({ name, op: "startup.phase", forceTransaction: true }, async (span) => {
            const ctx = span.spanContext();
            phase.data = {
                ...phase.data,
                "network.trace_id": ctx.traceId,
                "network.span_id": ctx.spanId,
            };
            return fn();
        });
    }
    finally {
        if (typeof phase.end === "undefined")
            phase.end = performance.now();
    }
}
const bundleStats = () => {
    const resources = performance.getEntriesByType("resource");
    const scripts = resources.filter((entry) => entry.initiatorType === "script" || /\.[mc]?js(\?|$)/.test(entry.name));
    const styles = resources.filter((entry) => entry.initiatorType === "link" || /\.css(\?|$)/.test(entry.name));
    const sum = (entries, key) => entries.reduce((total, entry) => total + entry[key], 0);
    return {
        scriptCount: scripts.length,
        scriptTransferKb: Math.round(sum(scripts, "transferSize") / 1024),
        scriptDecodedKb: Math.round(sum(scripts, "decodedBodySize") / 1024),
        cssTransferKb: Math.round(sum(styles, "transferSize") / 1024),
    };
};
/**
 * Log a set of phases as a console table: one row per phase, with each
 * phase's `data` spread into extra columns. `fallbackEnd` closes any phase
 * still ongoing.
 */
function logPhaseTable(phases, fallbackEnd) {
    console.table(phases.map((phase) => ({
        phase: phase.name,
        "start (ms)": Math.round(phase.start),
        "duration (ms)": Math.round((phase.end ?? fallbackEnd) - phase.start),
        ...phase.data,
    })));
}
/**
 * To be called once, when the app becomes usable. Sends the "app.startup"
 * transaction (when Sentry is initialized) and logs a summary in dev.
 */
export function reportAppLoaded(attributes) {
    if (reported)
        return;
    reported = true;
    const total = performance.now();
    phases.forEach((phase) => {
        if (typeof phase.end === "undefined")
            phase.end = total;
    });
    phases.sort((a, b) => a.start - b.start);
    const bundle = bundleStats();
    const navigation = performance.getEntriesByType("navigation")[0];
    const ttfb = navigation ? Math.round(navigation.responseStart) : undefined;
    if (shouldLogToConsole()) {
        console.info(`[load-perf] app usable in ${Math.round(total)}ms`, {
            ttfb,
            ...bundle,
        });
        logPhaseTable(phases, total);
    }
    if (!Sentry.getClient())
        return;
    const origin = performance.timeOrigin / 1000;
    // startNewTrace so this becomes a trace root: otherwise it joins the active
    // browser `pageload` trace and inherits its sampling decision, which is made
    // under the route name (not "app.startup") at the default rate — so the
    // tracesSampler rule that forces "app.startup" to 100% would never run.
    Sentry.startNewTrace(() => {
        const rootSpan = Sentry.startInactiveSpan({
            name: "app.startup",
            op: "app.load",
            forceTransaction: true,
            startTime: origin,
            attributes: {
                "bundle.script_count": bundle.scriptCount,
                "bundle.script_transfer_kb": bundle.scriptTransferKb,
                "bundle.script_decoded_kb": bundle.scriptDecodedKb,
                "bundle.css_transfer_kb": bundle.cssTransferKb,
                "network.effective_type": navigator.connection?.effectiveType,
                ...attributes,
            },
        });
        Sentry.withActiveSpan(rootSpan, () => {
            phases.forEach((phase) => {
                Sentry.startInactiveSpan({
                    name: phase.name,
                    op: "app.load.phase",
                    startTime: origin + phase.start / 1000,
                    attributes: phase.data,
                }).end(origin + (phase.end ?? total) / 1000);
            });
        });
        Sentry.setMeasurement("app.time_to_usable", Math.round(total), "millisecond", rootSpan);
        if (typeof ttfb !== "undefined")
            Sentry.setMeasurement("app.ttfb", ttfb, "millisecond", rootSpan);
        Sentry.setMeasurement("bundle.script_transfer", bundle.scriptTransferKb, "kilobyte", rootSpan);
        rootSpan.end(origin + total / 1000);
    });
}
/**
 */
export function reportPhasedTiming(name, start, end, phases, attributes) {
    if (shouldLogToConsole()) {
        console.info(`[load-perf] ${name}: ${Math.round(end - start)}ms`, attributes ?? "");
        logPhaseTable(phases, end);
    }
    if (!Sentry.getClient())
        return;
    const origin = performance.timeOrigin / 1000;
    // startNewTrace so the tracesSampler runs with this name (see reportAppLoaded)
    Sentry.startNewTrace(() => {
        const rootSpan = Sentry.startInactiveSpan({
            name,
            op: "app.load.phase",
            forceTransaction: true,
            startTime: origin + start / 1000,
            attributes,
        });
        Sentry.withActiveSpan(rootSpan, () => {
            phases.forEach((phase) => {
                Sentry.startInactiveSpan({
                    name: phase.name,
                    op: "app.load.phase",
                    startTime: origin + phase.start / 1000,
                    attributes: phase.data,
                }).end(origin + (phase.end ?? end) / 1000);
            });
        });
        rootSpan.end(origin + end / 1000);
    });
}
//# sourceMappingURL=loadPerf.js.map